/**
 * TimeSlotUtils.js
 * 
 * Pure utility functions for time slot calculations and management.
 * This module handles all time-related operations for the schedule system
 * with 15-minute granularity from 6 AM to 11 PM.
 * 
 * NO DEPENDENCIES: This file has zero external dependencies for maximum reusability and testability.
 * 
 * @module TimeSlotUtils
 */

// ==================== CONSTANTS ====================

/**
 * Time slot configuration constants
 */
export const TIME_SLOT_MINUTES = 15;
export const START_HOUR = 6;  // 6 AM
export const END_HOUR = 23;   // 11 PM (23:00, extended for better coverage)
export const TOTAL_SLOTS = (END_HOUR - START_HOUR + 1) * (60 / TIME_SLOT_MINUTES);

/**
 * Time slot boundaries for validation
 */
export const TIME_SLOT_BOUNDARIES = {
    MIN_SLOT_MINUTES: TIME_SLOT_MINUTES,
    MAX_SLOT_MINUTES: 60,
    MIN_HOUR: START_HOUR,
    MAX_HOUR: END_HOUR,
    TOTAL_SLOTS
};

/**
 * Shift duration constraints (in minutes)
 */
export const SHIFT_CONSTRAINTS = {
    MIN_DURATION: TIME_SLOT_MINUTES,        // 15 minutes
    MAX_DURATION: 16 * 60,                   // 16 hours
    STANDARD_DURATION: 8 * 60,               // 8 hours
    BREAK_THRESHOLD: 6 * 60                  // 6 hours (when break is typically required)
};

// ==================== TIME CONVERSION FUNCTIONS ====================

/**
 * Convert hours and minutes to slot number
 * 
 * @param {number} hours - Hour of day (0-23)
 * @param {number} [minutes=0] - Minutes (0-59)
 * @returns {number} Slot number (0-based index) or -1 if invalid
 * 
 * @example
 * timeToSlot(9, 30) // Returns 14 (9:30 AM is the 14th slot from 6:00 AM)
 * timeToSlot(6, 0)  // Returns 0 (first slot)
 * timeToSlot(5, 0)  // Returns -1 (before start hour)
 */
export const timeToSlot = (hours, minutes = 0) => {
    if (hours < START_HOUR || hours > END_HOUR) {
        return -1;
    }
    
    // Special case: END_HOUR with non-zero minutes is invalid
    if (hours === END_HOUR && minutes > 0) {
        return -1;
    }
    
    const totalMinutes = (hours - START_HOUR) * 60 + minutes;
    return Math.floor(totalMinutes / TIME_SLOT_MINUTES);
};

/**
 * Convert slot number to hours and minutes
 * 
 * @param {number} slot - Slot number (0-based index)
 * @returns {{hours: number, minutes: number}} Time object
 * 
 * @example
 * slotToTime(0)  // Returns { hours: 6, minutes: 0 }
 * slotToTime(14) // Returns { hours: 9, minutes: 30 }
 */
export const slotToTime = (slot) => {
    const totalMinutes = slot * TIME_SLOT_MINUTES;
    const hours = Math.floor(totalMinutes / 60) + START_HOUR;
    const minutes = totalMinutes % 60;
    
    return { hours, minutes };
};

/**
 * Convert time string (HH:MM) to total minutes since midnight
 * 
 * @param {string} timeString - Time in HH:MM format
 * @returns {number} Total minutes since midnight
 * 
 * @example
 * parseTimeString("09:30") // Returns 570
 * parseTimeString("14:45") // Returns 885
 */
export const parseTimeString = (timeString) => {
    if (!timeString || typeof timeString !== 'string') {
        return 0;
    }
    
    const [hours, minutes] = timeString.split(':').map(Number);
    
    if (isNaN(hours) || isNaN(minutes)) {
        return 0;
    }
    
    return hours * 60 + (minutes || 0);
};

/**
 * Convert minutes since midnight to time string (HH:MM)
 * 
 * @param {number} totalMinutes - Minutes since midnight
 * @returns {string} Time in HH:MM format
 * 
 * @example
 * minutesToTimeString(570) // Returns "09:30"
 * minutesToTimeString(885) // Returns "14:45"
 */
export const minutesToTimeString = (totalMinutes) => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

// ==================== FORMATTING FUNCTIONS ====================

/**
 * Format slot number to time string (HH:MM)
 * 
 * @param {number} slot - Slot number
 * @returns {string} Formatted time string
 * 
 * @example
 * formatTimeSlot(0)  // Returns "06:00"
 * formatTimeSlot(14) // Returns "09:30"
 */
export const formatTimeSlot = (slot) => {
    const { hours, minutes } = slotToTime(slot);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

/**
 * Format duration in minutes to human-readable string
 * 
 * @param {number} minutes - Duration in minutes
 * @param {boolean} [verbose=false] - Use verbose format (e.g., "2 hours 30 minutes" vs "2h 30m")
 * @returns {string} Formatted duration string
 * 
 * @example
 * formatDuration(90)          // Returns "1h 30m"
 * formatDuration(90, true)    // Returns "1 hour 30 minutes"
 * formatDuration(60)          // Returns "1h"
 * formatDuration(45)          // Returns "45m"
 */
export const formatDuration = (minutes, verbose = false) => {
    if (minutes === 0) {
        return verbose ? '0 minutes' : '0m';
    }
    
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (verbose) {
        const hourStr = hours > 0 ? `${hours} hour${hours !== 1 ? 's' : ''}` : '';
        const minStr = mins > 0 ? `${mins} minute${mins !== 1 ? 's' : ''}` : '';
        
        return [hourStr, minStr].filter(Boolean).join(' ');
    } else {
        const hourStr = hours > 0 ? `${hours}h` : '';
        const minStr = mins > 0 ? `${mins}m` : '';
        
        return [hourStr, minStr].filter(Boolean).join(' ');
    }
};

/**
 * Format duration in minutes for vacation displays
 * Specific format: "8h", "7h 45min", "0h"
 * 
 * @param {number} minutes - Duration in minutes
 * @returns {string} Formatted duration string
 * 
 * @example
 * formatDurationFromMinutes(480) // Returns "8h"
 * formatDurationFromMinutes(465) // Returns "7h 45min"
 * formatDurationFromMinutes(45)  // Returns "45min"
 * formatDurationFromMinutes(0)   // Returns "0h"
 */
export const formatDurationFromMinutes = (minutes) => {
    // Handle null/undefined/invalid values
    if (minutes === null || minutes === undefined || isNaN(minutes)) {
        return '0h';
    }
    
    // Ensure non-negative
    const totalMinutes = Math.max(0, Math.floor(minutes));
    
    // Zero case
    if (totalMinutes === 0) {
        return '0h';
    }
    
    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    
    // Hours only (no remaining minutes)
    if (mins === 0) {
        return `${hours}h`;
    }
    
    // Minutes only (less than 1 hour)
    if (hours === 0) {
        return `${mins}min`;
    }
    
    // Hours and minutes
    return `${hours}h ${mins}min`;
};

/**
 * Format duration in hours for vacation displays (migration helper)
 * Converts hours to minutes, then formats
 * 
 * @param {number} hours - Duration in hours (can be decimal)
 * @returns {string} Formatted duration string
 * 
 * @example
 * formatDurationFromHours(8)    // Returns "8h"
 * formatDurationFromHours(7.75) // Returns "7h 45min"
 * formatDurationFromHours(0.5)  // Returns "30min"
 * formatDurationFromHours(0)    // Returns "0h"
 */
export const formatDurationFromHours = (hours) => {
    // Handle null/undefined/invalid values
    if (hours === null || hours === undefined || isNaN(hours)) {
        return '0h';
    }
    
    // Convert hours to minutes
    const minutes = Math.max(0, Math.round(hours * 60));
    
    // Use the minutes formatter
    return formatDurationFromMinutes(minutes);
};

/**
 * Format time range from two slots
 * 
 * @param {number} startSlot - Start slot number
 * @param {number} endSlot - End slot number
 * @returns {string} Formatted time range (e.g., "09:00 - 17:30")
 * 
 * @example
 * formatTimeRange(12, 46) // Returns "09:00 - 17:30"
 */
export const formatTimeRange = (startSlot, endSlot) => {
    return `${formatTimeSlot(startSlot)} - ${formatTimeSlot(endSlot)}`;
};

// ==================== SLOT ID MANAGEMENT ====================

/**
 * Generate unique slot identifier
 * 
 * @param {string} staffId - Staff member ID
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {string} time - Time in HH:MM format
 * @returns {string} Unique slot ID
 * 
 * @example
 * generateSlotId("staff123", "2024-01-15", "09:30") // Returns "staff123-2024-01-15-09:30"
 */
export const generateSlotId = (staffId, date, time) => {
    return `${staffId}-${date}-${time}`;
};

/**
 * Parse slot ID into components
 * 
 * @param {string} slotId - Slot ID to parse
 * @returns {{staffId: string, date: string, time: string} | null} Parsed components or null if invalid
 * 
 * @example
 * parseSlotId("staff123-2024-01-15-09:30") 
 * // Returns { staffId: "staff123", date: "2024-01-15", time: "09:30" }
 */
export const parseSlotId = (slotId) => {
    if (!slotId || typeof slotId !== 'string') {
        return null;
    }
    
    const parts = slotId.split('-');
    
    // Expected format: staffId-YYYY-MM-DD-HH:MM (at least 5 parts)
    if (parts.length < 5) {
        return null;
    }
    
    // Reconstruct date (YYYY-MM-DD)
    const date = `${parts[1]}-${parts[2]}-${parts[3]}`;
    
    // Time is the last part
    const time = parts[4];
    
    // Staff ID is the first part
    const staffId = parts[0];
    
    return { staffId, date, time };
};

/**
 * Validate slot ID format
 * 
 * @param {string} slotId - Slot ID to validate
 * @returns {boolean} True if valid format
 */
export const isValidSlotId = (slotId) => {
    const parsed = parseSlotId(slotId);
    if (!parsed) return false;
    
    const { date, time } = parsed;
    
    // Validate date format (YYYY-MM-DD)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(date)) return false;
    
    // Validate time format (HH:MM)
    const timeRegex = /^\d{2}:\d{2}$/;
    if (!timeRegex.test(time)) return false;
    
    return true;
};

// ==================== SLOT GENERATION ====================

/**
 * Generate all time slots for a day
 * 
 * @returns {Array<{hour: number, minute: number, timeString: string, slotId: number}>} Array of time slot objects
 * 
 * @example
 * const slots = generateTimeSlots();
 * // Returns array with 73 slots from 06:00 to 23:00 (15-min intervals)
 */
export const generateTimeSlots = () => {
    const slots = [];
    
    for (let hour = START_HOUR; hour <= END_HOUR; hour++) {
        for (let minute = 0; minute < 60; minute += TIME_SLOT_MINUTES) {
            // Skip slots beyond END_HOUR (e.g., 23:15, 23:30, 23:45)
            if (hour === END_HOUR && minute > 0) {
                break;
            }
            
            slots.push({
                hour,
                minute,
                timeString: `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`,
                slotId: timeToSlot(hour, minute)
            });
        }
    }
    
    return slots;
};

/**
 * Generate time slots for a specific hour
 * 
 * @param {number} hour - Hour of day (0-23)
 * @returns {Array<{hour: number, minute: number, timeString: string, slotId: number}>} Array of time slot objects for that hour
 * 
 * @example
 * generateTimeSlotsForHour(9)
 * // Returns [{hour: 9, minute: 0, ...}, {hour: 9, minute: 15, ...}, ...]
 */
export const generateTimeSlotsForHour = (hour) => {
    if (hour < START_HOUR || hour > END_HOUR) {
        return [];
    }
    
    const slots = [];
    
    for (let minute = 0; minute < 60; minute += TIME_SLOT_MINUTES) {
        // Skip slots beyond END_HOUR
        if (hour === END_HOUR && minute > 0) {
            break;
        }
        
        slots.push({
            hour,
            minute,
            timeString: `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`,
            slotId: timeToSlot(hour, minute)
        });
    }
    
    return slots;
};

// ==================== SLOT SELECTION CALCULATIONS ====================

/**
 * Calculate continuous slot range between two slot IDs
 * 
 * @param {string} startSlotId - Starting slot ID
 * @param {string} endSlotId - Ending slot ID
 * @param {string} staffId - Staff ID for validation
 * @returns {string[]} Array of slot IDs in range (sorted)
 * 
 * @example
 * calculateSelectedSlots("staff1-2024-01-15-09:00", "staff1-2024-01-15-11:00", "staff1")
 * // Returns array of 9 slot IDs from 09:00 to 11:00 (inclusive)
 */
export const calculateSelectedSlots = (startSlotId, endSlotId, staffId) => {
    if (!startSlotId || !endSlotId) {
        return [];
    }
    
    const startParsed = parseSlotId(startSlotId);
    const endParsed = parseSlotId(endSlotId);
    
    // Validation: same staff, same date
    if (!startParsed || !endParsed || 
        startParsed.staffId !== endParsed.staffId || 
        startParsed.date !== endParsed.date || 
        startParsed.staffId !== staffId) {
        return [startSlotId];
    }
    
    const startMinutes = parseTimeString(startParsed.time);
    const endMinutes = parseTimeString(endParsed.time);
    
    // Determine direction (forward or backward selection)
    const actualStart = Math.min(startMinutes, endMinutes);
    const actualEnd = Math.max(startMinutes, endMinutes);
    
    const selectedSlots = [];
    
    // Generate all slots in range
    for (let minutes = actualStart; minutes <= actualEnd; minutes += TIME_SLOT_MINUTES) {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        
        // Only include valid business hours
        if (hours >= START_HOUR && hours <= END_HOUR) {
            // Skip slots beyond END_HOUR
            if (hours === END_HOUR && mins > 0) {
                break;
            }
            
            const timeString = `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
            selectedSlots.push(generateSlotId(staffId, startParsed.date, timeString));
        }
    }
    
    return selectedSlots;
};

/**
 * Calculate total duration from selected slots
 * 
 * @param {string[]} selectedSlots - Array of slot IDs
 * @returns {{hours: number, minutes: number, totalMinutes: number}} Duration breakdown
 * 
 * @example
 * calculateHoursFromSelection(["slot1", "slot2", "slot3", "slot4"])
 * // Returns { hours: 1, minutes: 0, totalMinutes: 60 } for 4 x 15-min slots
 */
export const calculateHoursFromSelection = (selectedSlots) => {
    const totalMinutes = selectedSlots.length * TIME_SLOT_MINUTES;
    
    return {
        hours: Math.floor(totalMinutes / 60),
        minutes: totalMinutes % 60,
        totalMinutes
    };
};

/**
 * Calculate duration between two slot IDs
 * 
 * @param {string} startSlotId - Starting slot ID
 * @param {string} endSlotId - Ending slot ID (exclusive)
 * @returns {number} Duration in minutes
 * 
 * @example
 * calculateSlotDuration("staff1-2024-01-15-09:00", "staff1-2024-01-15-11:00")
 * // Returns 120 (2 hours)
 */
export const calculateSlotDuration = (startSlotId, endSlotId) => {
    const startParsed = parseSlotId(startSlotId);
    const endParsed = parseSlotId(endSlotId);
    
    if (!startParsed || !endParsed) {
        return 0;
    }
    
    const startMinutes = parseTimeString(startParsed.time);
    const endMinutes = parseTimeString(endParsed.time);
    
    return Math.abs(endMinutes - startMinutes);
};

// ==================== SLOT QUERIES ====================

/**
 * Check if a slot is within business hours
 * 
 * @param {number} hour - Hour to check
 * @param {number} minute - Minute to check
 * @returns {boolean} True if within business hours
 * 
 * @example
 * isSlotWithinBusinessHours(9, 30)  // Returns true
 * isSlotWithinBusinessHours(5, 0)   // Returns false (before start)
 * isSlotWithinBusinessHours(23, 30) // Returns false (after end)
 */
export const isSlotWithinBusinessHours = (hour, minute = 0) => {
    if (hour < START_HOUR || hour > END_HOUR) {
        return false;
    }
    
    // END_HOUR with non-zero minutes is outside business hours
    if (hour === END_HOUR && minute > 0) {
        return false;
    }
    
    return true;
};

/**
 * Get the nearest valid slot for a given time
 * 
 * @param {number} hour - Hour of day
 * @param {number} minute - Minute of day
 * @returns {{hour: number, minute: number}} Nearest valid slot
 * 
 * @example
 * getNearestSlot(9, 37) // Returns { hour: 9, minute: 45 } (rounds to nearest 15-min)
 * getNearestSlot(5, 30) // Returns { hour: 6, minute: 0 } (clamps to start hour)
 */
export const getNearestSlot = (hour, minute) => {
    // Clamp hour to business hours
    let adjustedHour = Math.max(START_HOUR, Math.min(END_HOUR, hour));
    
    // Round minute to nearest slot
    let adjustedMinute = Math.round(minute / TIME_SLOT_MINUTES) * TIME_SLOT_MINUTES;
    
    // Handle minute overflow
    if (adjustedMinute >= 60) {
        adjustedMinute = 0;
        adjustedHour += 1;
        
        // Check if we've exceeded END_HOUR
        if (adjustedHour > END_HOUR) {
            adjustedHour = END_HOUR;
            adjustedMinute = 0;
        }
    }
    
    // Handle END_HOUR constraint
    if (adjustedHour === END_HOUR && adjustedMinute > 0) {
        adjustedMinute = 0;
    }
    
    return { hour: adjustedHour, minute: adjustedMinute };
};

/**
 * Get slot number for current time
 * 
 * @param {Date} [date=new Date()] - Date object (defaults to now)
 * @returns {number} Current slot number or -1 if outside business hours
 * 
 * @example
 * getCurrentSlot() // Returns slot number for current time
 * getCurrentSlot(new Date("2024-01-15T09:30:00")) // Returns 14
 */
export const getCurrentSlot = (date = new Date()) => {
    return timeToSlot(date.getHours(), date.getMinutes());
};

/**
 * Check if current time is within business hours
 * 
 * @param {Date} [date=new Date()] - Date object (defaults to now)
 * @returns {boolean} True if current time is within business hours
 */
export const isCurrentlyBusinessHours = (date = new Date()) => {
    return getCurrentSlot(date) !== -1;
};

// ==================== SLOT COMPARISON ====================

/**
 * Compare two slot IDs chronologically
 * 
 * @param {string} slotId1 - First slot ID
 * @param {string} slotId2 - Second slot ID
 * @returns {number} -1 if slot1 < slot2, 0 if equal, 1 if slot1 > slot2
 * 
 * @example
 * compareSlots("staff1-2024-01-15-09:00", "staff1-2024-01-15-10:00") // Returns -1
 */
export const compareSlots = (slotId1, slotId2) => {
    const parsed1 = parseSlotId(slotId1);
    const parsed2 = parseSlotId(slotId2);
    
    if (!parsed1 || !parsed2) {
        return 0;
    }
    
    // Compare dates first
    if (parsed1.date < parsed2.date) return -1;
    if (parsed1.date > parsed2.date) return 1;
    
    // Same date, compare times
    const time1 = parseTimeString(parsed1.time);
    const time2 = parseTimeString(parsed2.time);
    
    if (time1 < time2) return -1;
    if (time1 > time2) return 1;
    
    return 0;
};

/**
 * Check if two slots are adjacent (15 minutes apart)
 * 
 * @param {string} slotId1 - First slot ID
 * @param {string} slotId2 - Second slot ID
 * @returns {boolean} True if slots are adjacent
 */
export const areSlotsAdjacent = (slotId1, slotId2) => {
    const parsed1 = parseSlotId(slotId1);
    const parsed2 = parseSlotId(slotId2);
    
    if (!parsed1 || !parsed2 || parsed1.date !== parsed2.date) {
        return false;
    }
    
    const time1 = parseTimeString(parsed1.time);
    const time2 = parseTimeString(parsed2.time);
    
    return Math.abs(time1 - time2) === TIME_SLOT_MINUTES;
};

// ==================== EXPORT CONFIGURATION ====================

/**
 * Get all time slot configuration constants
 * 
 * @returns {Object} Configuration object
 */
export const getTimeSlotConfig = () => {
    return {
        TIME_SLOT_MINUTES,
        START_HOUR,
        END_HOUR,
        TOTAL_SLOTS,
        SHIFT_CONSTRAINTS
    };
};

/**
 * Export all utility functions as a single object (for destructuring)
 */
export const TimeSlotUtils = {
    // Constants
    TIME_SLOT_MINUTES,
    START_HOUR,
    END_HOUR,
    TOTAL_SLOTS,
    TIME_SLOT_BOUNDARIES,
    SHIFT_CONSTRAINTS,
    
    // Time conversion
    timeToSlot,
    slotToTime,
    parseTimeString,
    minutesToTimeString,
    
    // Formatting
    formatTimeSlot,
    formatDuration,
    formatDurationFromMinutes,
    formatDurationFromHours,
    formatTimeRange,
    
    // Slot ID management
    generateSlotId,
    parseSlotId,
    isValidSlotId,
    
    // Slot generation
    generateTimeSlots,
    generateTimeSlotsForHour,
    
    // Selection calculations
    calculateSelectedSlots,
    calculateHoursFromSelection,
    calculateSlotDuration,
    
    // Slot queries
    isSlotWithinBusinessHours,
    getNearestSlot,
    getCurrentSlot,
    isCurrentlyBusinessHours,
    
    // Slot comparison
    compareSlots,
    areSlotsAdjacent,
    
    // Configuration
    getTimeSlotConfig
};

export default TimeSlotUtils;